import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';
import type {
  AnalyticsV2CategoriesQueryParams,
  AnalyticsV2QueryParams,
  AnalyticsV2SeriesQueryParams,
  CategoryTableRow,
  CoverageFunnelResult,
  ScalarMetricKey,
  ScalarMetricsBatch,
  SeriesMetricKey,
  SeriesMetricsBatch
} from '@/types/analyticsV2';

/**
 * Brand scope on the wire.
 *
 * `buId`, camelCase, because that is the name kim-analytics' schema declares. Everywhere
 * else in the platform the wire name is `bu_id`, and the two proxies in front of
 * kim-analytics forward whatever they are given, so the spelling has to be right here.
 *
 * It fails loudly if it is not: every analytics-v2 querystring schema is
 * `additionalProperties: false`, so `bu_id` reaches kim-analytics and 400s as an unknown
 * parameter rather than being ignored. That also means the accepted param set is fixed end
 * to end — anything new added along this chain has to be declared in that schema too.
 *
 * Omitted rather than sent empty when unscoped: kim-analytics reads a missing `buId` as
 * the whole account, while `buId=` fails its positive-integer pattern.
 */
const buScopeParam = (buId: string | null | undefined): { buId?: string } =>
  buId ? { buId } : {};

// Reads go through machine's /admin/analytics relay to kim-analytics. Both that relay and
// kim-ai-backend behind it forward the query string generically, so `buId` needs no change
// in either.
export const analyticsV2Service = {
  /** Fetches scalar metrics in one call, keyed by metric key. */
  async getMetrics(
    keys: ScalarMetricKey[],
    params: AnalyticsV2QueryParams
  ): Promise<ScalarMetricsBatch> {
    const res = await api.get<ApiResponse<ScalarMetricsBatch>>(
      API_ENDPOINTS.ANALYTICS.METRICS,
      {
        params: {
          keys: keys.join(','),
          fromDate: params.fromDate,
          toDate: params.toDate,
          accountId: params.accountId,
          ...buScopeParam(params.buId)
        }
      }
    );
    return res.data.data;
  },

  /** Same as getMetrics, but each series is bucketed by granularity. */
  async getSeries(
    keys: SeriesMetricKey[],
    params: AnalyticsV2SeriesQueryParams
  ): Promise<SeriesMetricsBatch> {
    const res = await api.get<ApiResponse<SeriesMetricsBatch>>(
      API_ENDPOINTS.ANALYTICS.METRICS_SERIES,
      {
        params: {
          keys: keys.join(','),
          fromDate: params.fromDate,
          toDate: params.toDate,
          granularity: params.granularity,
          accountId: params.accountId,
          ...buScopeParam(params.buId)
        }
      }
    );
    return res.data.data;
  },

  /** One row per category for the Ticket Metrics table. */
  async getCategories(
    params: AnalyticsV2CategoriesQueryParams
  ): Promise<CategoryTableRow[]> {
    const res = await api.get<ApiResponse<CategoryTableRow[]>>(
      API_ENDPOINTS.ANALYTICS.CATEGORIES,
      {
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          ...(params.ticketCategory
            ? { ticketCategory: params.ticketCategory }
            : {}),
          accountId: params.accountId,
          ...buScopeParam(params.buId)
        }
      }
    );
    return res.data.data;
  },

  /** Coverage funnel stages plus the HITL approved/rejected/pending split. */
  async getFunnel(
    params: AnalyticsV2QueryParams
  ): Promise<CoverageFunnelResult> {
    const res = await api.get<ApiResponse<CoverageFunnelResult>>(
      API_ENDPOINTS.ANALYTICS.FUNNEL,
      {
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          accountId: params.accountId,
          ...buScopeParam(params.buId)
        }
      }
    );
    return res.data.data;
  }
};
